import { Quaternion } from '@technology/web3d';
import type {
  ScanDataApiService,
  TcRegion,
} from '@data-access/geospatial/scan-data';
import type { ProjectDataState } from '@mobile-mapping/providers';
import type { PlanarPaneContent } from '@mobile-mapping/web3d';
import { LoadState } from '@trimble/data-loading';
import type {
  PanoramaStation,
  StationsCapabilityProvider,
} from '@trimble/web3d-providers';
import { produce, type SetStoreFunction } from 'solid-js/store';
import {
  calculateStationHeadingOffset,
  getStationTransformWithHeadingOffset,
  zAxisQuaternionFromDegrees,
} from './sds-station-mapper';

const ROTATION_INCREMENT_DEGREES = 90;

export interface RotateSdsPanoramasParams {
  readonly pointCloudId: string;
  readonly planarContent: PlanarPaneContent;
  readonly region: TcRegion;
  readonly scanDataApi: ScanDataApiService;
  readonly stationsProvider: StationsCapabilityProvider;
  readonly dataState: ProjectDataState;
  readonly setDataState: SetStoreFunction<ProjectDataState>;
  /** Optional override; defaults to +90°. */
  readonly headingOffsetDeltaDegrees?: number;
}

/**
 * Rotates the panoramas of an SDS point cloud by `headingOffsetDeltaDegrees`
 * (default 90°). Mirrors TRCPS's `RotatePanoramas` flow but performs the
 * update **in place**:
 *
 *   1. Compute the new station transform (heading += delta) and optimistically
 *      apply it to `dataState.pointCloud[id].stationTransform` so the menu
 *      label and any other consumers reflect the new heading immediately.
 *   2. Persist via `PATCH /pointclouds/{id}` and invalidate the single
 *      `getPointCloud` query so future reads pick up the new value.
 *   3. Update the stations provider's panorama entries for this run in place
 *      — same IDs, same indices, new quaternions. This preserves
 *      `activeRunId`, every pane's `selectedStationIndex`, and consequently
 *      the cross-pane sync. `updatePanoramaStationsForRun` produces a new
 *      `stationList` array reference (and bumps the camera's cache version),
 *      which `ImageCapabilityBase`'s loading effect detects: each loaded
 *      model at an index whose station object reference changed is unloaded
 *      and re-loaded with the new metadata. No direct viewer access from
 *      this orchestrator.
 *   4. On API failure the optimistic dataState update is rolled back; the
 *      provider state is left untouched (nothing was changed yet).
 */
export async function rotateSdsPanoramas(
  params: RotateSdsPanoramasParams
): Promise<void> {
  const {
    pointCloudId,
    planarContent,
    region,
    scanDataApi,
    stationsProvider,
    dataState,
    setDataState,
    headingOffsetDeltaDegrees = ROTATION_INCREMENT_DEGREES,
  } = params;

  if (dataState.pointCloud.state !== LoadState.LoadComplete) {
    return;
  }
  const pointCloud = dataState.pointCloud.items[pointCloudId];
  if (!pointCloud) {
    return;
  }

  const previousTransform = pointCloud.stationTransform;
  const previousHeading = calculateStationHeadingOffset(previousTransform);
  const newHeading = previousHeading + headingOffsetDeltaDegrees;
  const newTransform = getStationTransformWithHeadingOffset(
    previousTransform,
    newHeading
  );

  setDataState(
    'pointCloud',
    produce((slice) => {
      if (slice.state !== LoadState.LoadComplete) return;
      const item = slice.items[pointCloudId];
      if (!item) return;
      item.stationTransform = newTransform;
    })
  );

  try {
    await scanDataApi.updateStationTransform(
      region,
      planarContent.selectedProjectId,
      pointCloudId,
      newTransform
    );
  } catch (error) {
    setDataState(
      'pointCloud',
      produce((slice) => {
        if (slice.state !== LoadState.LoadComplete) return;
        const item = slice.items[pointCloudId];
        if (!item) return;
        item.stationTransform = previousTransform;
      })
    );
    throw error;
  }

  applyPanoramaRotationLocally({
    runId: planarContent.selectedRunId,
    previousHeading,
    newHeading,
    stationsProvider,
  });
}

/**
 * Reorients every panorama station in the provider that belongs to `runId`
 * by `newHeading - previousHeading`, preserving station IDs / indices.
 * Model unload + reload is handled downstream by `ImageCapabilityBase`'s
 * loading effect, which detects the in-place mutation as a stationList
 * reference change and a per-index station object reference change.
 *
 * Since both prior and target headings are pure Z-axis rotations (yaw),
 * `H(new) = H(delta) ∘ H(prev)` and we can compose the delta directly onto
 * each station's current quaternion — no need to round-trip through the
 * original station data. The (un)permutation steps match
 * `applyHeadingToQuaternion` in `sds-station-mapper`, which is the inverse
 * we'd apply on a cold load.
 */
function applyPanoramaRotationLocally(args: {
  readonly runId: string;
  readonly previousHeading: number;
  readonly newHeading: number;
  readonly stationsProvider: StationsCapabilityProvider;
}): void {
  const { runId, previousHeading, newHeading, stationsProvider } = args;

  const headingDelta = newHeading - previousHeading;
  const deltaQuaternion = zAxisQuaternionFromDegrees(headingDelta);

  stationsProvider.updatePanoramaStationsForRun(runId, (station) =>
    rotateStationQuaternion(station, deltaQuaternion)
  );
}

/**
 * Pre-multiplies `station.quaternion` by `deltaQuaternion`, applying the
 * same LodAnalyzer permutation convention as `applyHeadingToQuaternion` in
 * `sds-station-mapper.ts` — un-permute `(x,y,z,w) → (z,w,x,y)`, multiply,
 * re-permute. The output is the quaternion the station would have had if
 * mapped fresh with the new heading.
 *
 * If the station's quaternion is missing any component, the station is
 * returned unchanged (same reference) so callers can identity-skip.
 *
 * Pure helper — exported for testing.
 */
export function rotateStationQuaternion(
  station: PanoramaStation,
  deltaQuaternion: Quaternion
): PanoramaStation {
  const q = station.quaternion;
  if (
    q?.x === undefined ||
    q?.y === undefined ||
    q?.z === undefined ||
    q?.w === undefined
  ) {
    return station;
  }
  const unpermuted = new Quaternion(q.z, q.w, q.x, q.y);
  const rotated = deltaQuaternion.clone().multiply(unpermuted).normalize();

  return {
    ...station,
    quaternion: {
      x: rotated.z,
      y: rotated.w,
      z: rotated.x,
      w: rotated.y,
    },
  };
}
